<template>
  <div
    v-if="products.length"
    class="product-addons"
    :class="{
      [$class('active')]: Boolean(selectedProduct),
    }"
  >
    <accordion-item
      :active="isActive"
      :title="title"
      transition-name="fade-height"
      :heading-level="2"
      @toggle-accordion="handleAccordionClick"
    >
      <template #icon>
        <span
          class="text-other-button-regular"
          v-text="secondaryLabel"
        />
      </template>
      <template #closeIcon>
        <span
          class="text-other-button-regular"
          v-text="secondaryLabel"
        />
      </template>

      <template #content>
        <div
          v-for="addon of products"
          :key="addon.id"
          class="product-addons__product flex"
        >
          <div>
            <responsive-image
              class="product-addons__image images--multiply button--reset"
              :image="addon.featuredImage"
              min-max="80-160"
              ratio="1:1"
              sizes="80px"
              tag="button"
              type="button"
              :aria-label="`Open quick view drawer for ${addon.title}`"
              @click="openQuickAdd(addon)"
            />
          </div>

          <p class="product-addons__details flex flex--col">
            <button
              class="button--reset text-left"
              type="button"
              :aria-label="`Open quick view drawer for ${addon.title}`"
              @click="openQuickAdd(addon)"
              v-html="addon.title"
            />
            <product-prices
              v-if="addon"
              :price="addon.price.amount"
              :compare-at-price="addon.compareAtPrice.amount"
              element="span"
            />
          </p>

          <div class="product-addons__cta flex">
            <btn
              v-if="addon.handle !== selectedProduct?.handle"
              :label="getAddLabel(addon)"
              modifiers="secondary block center"
              type="button"
              @click="selectedProduct = addon"
            />
            <btn
              v-else
              :label="$string('product.add_ons.remove')"
              modifiers="text"
              type="button"
              @click="removeSelectedProduct"
            />
          </div>
        </div>
      </template>
    </accordion-item>

    <teleport to="body">
      <overlay
        key="addOnsDrawer"
        type="drawer"
        namespace="addOnsDrawer"
        direction="left"
        block-class="product-addons-overlay"
        :show-close="false"
      >
        <template #body="overlay">
          <product-addons-drawer
            :overlay="overlay"
            :product="product"
          />
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>
/**
 * Vue: Product addons (product-addons)
 * -----------------------------------------------------------------------------
 * Additional products on the product form that allow the user to bundle with
 * the main product when adding to cart.
 *
 */

import { mapActions, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import Overlay from '~global/overlay/overlay'
import ProductPrices from '~global/product-prices/product-prices'
import ResponsiveImage from '~global/images/responsive-image'

import AccordionItem from '~async/basic-accordion/accordion-item'
import ProductAddonsDrawer from '~async/product-addons/product-addons-drawer'

import productsQuery from '~graphql/products.gql'

import { formatEcommerceEvent, track } from '~/helpers/datalayer-actions'
import { getMetafieldIDs, getVariables } from '~/helpers/graphql'
import { formatProduct } from '~/helpers/format'
import { getAddOns } from '~/helpers/product'

export default {
  name: 'ProductAddons',

  components: {
    AccordionItem,
    Btn,
    Overlay,
    ProductAddonsDrawer,
    ProductPrices,
    ResponsiveImage,
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update:add-on-variant': (payload) => typeof payload === 'object' || payload === null,
  },

  data() {
    return {
      isActive: false,
      selectedProduct: null,
      queriedProducts: [false],
    }
  },

  computed: {
    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      lastAddedItem: (state) => state.cart.lastAddedItem,
    }),

    /**
     * Get add on metaobject from product metafields
     * @returns {Object}
     */
    metaobject() {
      return getAddOns(this.product)
    },

    /**
     * Compute add on products
     * @returns {Array}
     */
    products() {
      if (this.selectedProduct) {
        return [this.selectedProduct]
      }

      if (!this.metaobject) {
        return []
      }

      if (!this.product.liquid) {
        return this.queriedProducts
      }

      return (
        this.metaobject.products?.filter((product) => {
          return product?.available
        }) || []
      )
    },

    /**
     * Compute label on the right side of the accordion title
     * @returns {String}
     */
    secondaryLabel() {
      if (this.selectedProduct) {
        return this.$string('product.add_ons.edit')
      }

      if (this.isActive) {
        return this.$string('product.add_ons.close')
      }

      return this.$string('product.add_ons.open')
    },

    /**
     * Compute main accordion title
     * @returns {String}
     */
    title() {
      /**
       * Add-on selected state (open)
       */
      if (this.selectedProduct) {
        const price = this.$formatMoney(this.selectedProduct.price.amount)
        let str = this.$string('product.add_ons.complete_title', {
          replace: {
            price,
          },
        })

        if (this.metaobject?.complete_title) {
          str = this.metaobject.complete_title.replace('{{ price }}', price)
        }
        return str
      }

      /**
       * Opened state
       */
      if (this.isActive) {
        return this.metaobject?.opened_title || this.$string('product.add_ons.opened_title')
      }

      /**
       * Default closed state
       */
      return this.metaobject?.title || this.$string('product.add_ons.title')
    },
  },

  watch: {
    /**
     * Watch for changes in metaobject to query products.
     * Products should be queried if the products array is a list of GIDs
     */
    metaobject(meta, oldMeta) {
      const products = meta?.products || []
      const oldProducts = oldMeta?.products || []
      if (
        this.product.liquid ||
        typeof products[0] !== 'string' ||
        !products[0]?.includes('gid') ||
        JSON.stringify(products) === JSON.stringify(oldProducts)
      ) {
        return
      }
      this.queryProducts()
    },

    /**
     * Emit add on variant and product when updated
     */
    selectedProduct(product) {
      this.$emit(
        'update:add-on-variant',
        this.selectedProduct
          ? {
              variant: this.selectedProduct.variants[0] || null,
              product: this.selectedProduct,
            }
          : null,
      )

      let payload = {
        event: `pdp_add_on_${product ? 'add' : 'remove'}`,
      }
      if (product) {
        payload = {
          ...payload,
          ...formatEcommerceEvent({
            product: this.selectedProduct,
          }),
        }
      }
      track(payload)
    },
  },

  created() {
    if (this.product.liquid) {
      return
    }
    this.queryProducts()
  },

  methods: {
    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openOverlay: 'overlays/open',
      setProduct: 'quick-add/setProduct',
    }),

    /**
     * Generate "add" label. Displays a separate label for items already
     * in cart.
     * @param {object} addon - Addon product
     * @returns {string}
     */
    getAddLabel(addon) {
      const variantInCart =
        this.cart?.items?.some((item) => {
          return item.variant_id === addon.variants[0]?.id
        }) || false

      if (variantInCart) {
        return this.$string('product.add_ons.in_bag')
      }

      return this.$string('product.add_ons.add')
    },

    /**
     * - If a product has been selected, remove the selection on "edit" click
     * and display all products. The accordion remains open.
     * - Otherwise, toggle accordion open and close state
     */
    handleAccordionClick() {
      track({
        event: 'pdp_add_on_toggle',
        opened: !this.isActive,
      })
      if (this.selectedProduct) {
        this.selectedProduct = null
        return
      }

      this.isActive = !this.isActive
    },

    /**
     * Open quick add drawer
     * @param {object} addonProduct
     */
    openQuickAdd(addonProduct) {
      this.setProduct(addonProduct)
      this.openOverlay({
        component: 'quick-add-drawer',
        ignoreDismissed: true,
        namespace: 'quickAddDrawer',
      })
      track({
        event: 'pdp_add_on_quick_add_view',
        ...formatEcommerceEvent({
          product: addonProduct,
        }),
      })
    },

    /**
     * Fetch products using GIDs
     */
    async queryProducts() {
      const ids = this.metaobject?.products
      if (!ids.length) {
        return
      }

      try {
        this.selectedProduct = null
        this.queriedProducts = [false]

        await this.$nextTick()

        const response = await cnvs.Query({
          query: productsQuery,
          variables: getVariables({
            ids,
            metafieldIds: [...getMetafieldIDs('product'), ...getMetafieldIDs('variant')],
          }),
        })

        this.queriedProducts = response.nodes
          .map((product) => {
            return formatProduct({ product })
          })
          .filter((product) => {
            return product && product.available
          })
      } catch (error) {
        cnvs.ReportError('[Product Add On - Product Query]', error)
      }
    },

    /**
     * When "remove" is clicked, remove the selected product and close the
     * accordion.
     */
    async removeSelectedProduct() {
      this.isActive = false
      await this.$nextTick()
      this.selectedProduct = null
    },
  },
}
</script>

<style lang="scss">
@import './product-addons';
</style>
